// CRM layer — frontend API client (added Phase 6-B).
//
// Reuses the app's existing axios instance, so the JWT interceptor and the
// 401-redirect behaviour are identical to the rest of the app. Nothing here
// touches the existing endpoints.

import api from '../services/api';

// --- opportunities ----------------------------------------------------------
export const crmSuggest = (phone, queue) =>
  api.get(`/opportunities/suggest/${encodeURIComponent(phone)}`, { params: queue ? { queue } : {} });

export const crmCreateOpportunity = (data) => api.post('/opportunities', data);
export const crmGetOpportunity = (id) => api.get(`/opportunities/${id}`);
export const crmListOpportunities = (params) => api.get('/opportunities', { params });
export const crmMyOpportunities = () => api.get('/opportunities/mine');
export const crmSetStage = (id, stage) => api.put(`/opportunities/${id}/stage`, { stage });

// Batch 3: the human decision on an auto-created opportunity.
// classification: 'real' | 'prior_followup'   (two answers only)
// opportunity_type is only meaningful for 'real' and stays optional — the
// backend accepts the classification without it.
export const crmClassify = (id, classification, extra = {}) =>
  api.put(`/opportunities/${id}/classify`, { classification, ...extra });
export const crmRevertStage = (id, stage, note) => api.put(`/opportunities/${id}/revert`, { stage, note });
export const crmSetDetails = (id, request_details) => api.put(`/opportunities/${id}/details`, { request_details });
export const crmAssign = (id, assigned_to_extension) =>
  api.put(`/opportunities/${id}/assign`, { assigned_to_extension });
export const crmGetMessages = (id) => api.get(`/opportunities/${id}/messages`);
export const crmPostMessage = (id, message) => api.post(`/opportunities/${id}/messages`, { message });

// --- clients ----------------------------------------------------------------
// Browsable directory: ?search= (name or phone) &classification=
export const crmListClients = (params) => api.get('/clients', { params });
export const crmGetClient = (phone) => api.get(`/clients/${encodeURIComponent(phone)}`);
export const crmSaveClient = (phone, data) => api.put(`/clients/${encodeURIComponent(phone)}`, data);

// The OLD contacts store (backend/routes/contacts.js, `contacts` table) is a
// separate record keyed on the same phone number. The CRM profile surfaces it
// read-only so data saved before the CRM existed is never lost from view.
// Returns { exists: false } when there is no old record.
export const crmLookupOldContact = (phone) => api.get(`/contacts/lookup/${encodeURIComponent(phone)}`);

// --- reference data ---------------------------------------------------------
export const crmGetTypes = (includeInactive) =>
  api.get('/crm/types', { params: includeInactive ? { include_inactive: 1 } : {} });
export const crmCreateType = (data) => api.post('/crm/types', data);
export const crmUpdateType = (id, data) => api.put(`/crm/types/${id}`, data);

export const crmGetSettings = () => api.get('/crm/settings');
export const crmSaveSettings = (settings) => api.put('/crm/settings', { settings });

export const crmGetUserDepartments = () => api.get('/crm/user-departments');
export const crmAddUserDepartment = (extension, department) =>
  api.post('/crm/user-departments', { extension, department });
export const crmDeleteUserDepartment = (id) => api.delete(`/crm/user-departments/${id}`);

// --- admin views ------------------------------------------------------------
export const crmGetDashboard = (params) => api.get('/crm/dashboard', { params });
export const crmGetUnassigned = () => api.get('/crm/unassigned');
export const crmGetCrmAlerts = (limit) => api.get('/crm/alerts', { params: { limit: limit || 15 } });

// --- transfers --------------------------------------------------------------
export const crmCreateTransfer = (data) => api.post('/crm/transfers', data);
export const crmIncomingTransfers = () => api.get('/crm/transfers/incoming');
export const crmTransferCount = () => api.get('/crm/transfers/count');
export const crmPickupTransfer = (id) => api.put(`/crm/transfers/${id}/pickup`);
export const crmDoneTransfer = (id) => api.put(`/crm/transfers/${id}/done`);

// --- manager views (Batch 2, admin) -----------------------------------------
// Read-only monitoring of every transfer, with names resolved (GET all).
export const crmAllTransfers = (params) => api.get('/crm/transfers/all', { params });
// View-as-employee (read-only): a chosen agent's pipeline / inbox.
export const crmAsUserOpportunities = (ext) => api.get(`/crm/as-user/${encodeURIComponent(ext)}/opportunities`);
export const crmAsUserTransfers = (ext) => api.get(`/crm/as-user/${encodeURIComponent(ext)}/transfers`);

// Minimal extension->name directory (any authenticated user; live since the
// Batch 2 restart). Used by manager pickers and CRM name resolution.
export const crmUserNames = () => api.get('/auth/users/names');

// --- shared display helpers -------------------------------------------------
export const STAGE_LABEL = {
  new: 'جديد',
  offer_sent: 'تم إرسال العرض',
  serious: 'جاد',
  not_serious: 'غير جاد',
  contracted: 'تم التعاقد',
  lost: 'خسارة',
  // Batch 3: a PRE-stage, not a rung on the ladder. Listed here so any screen
  // that renders STAGE_LABEL[stage] shows words instead of the raw enum.
  pending_classification: 'بانتظار التصنيف'
};

// ---------------------------------------------------------------------------
// Batch 3 — auto-created opportunities and their classification.
//
// Mirrors backend/crm/utils/classification.js. The server is the enforcer; this
// copy only decides what to render, so a stale copy can mislabel but never
// grant anything.
// ---------------------------------------------------------------------------
export const PENDING_STAGE = 'pending_classification';

// The placeholder the auto-scan writes into the NOT NULL opportunity_type
// column. It is not a row in the type catalogue and must never be shown raw.
export const UNCLASSIFIED_TYPE = 'غير مصنف';

export const isPendingClassification = (o) => !!o && o.crm_stage === PENDING_STAGE;

/** Render an opportunity_type, turning the sentinel into a clean label. */
export const typeLabel = (t) => (t === UNCLASSIFIED_TYPE || !t ? 'غير مصنّف' : t);

// Display names for communication_type. Keeps 'inquiry'/'question' even though
// neither can be chosen any more: rows classified before the simplification
// still carry them, and the detail card reads this map to render them in Arabic
// instead of a raw English key.
export const COMMUNICATION_LABEL = {
  real: 'فرصة حقيقية',
  prior_followup: 'متابعة طلب سابق',
  inquiry: 'استفسار',
  question: 'سؤال'
};

// The two quick choices, in the order they are presented. `archives: true`
// means picking it closes the opportunity immediately and returns to the list.
//
// Deliberately two, not four: 'استفسار' and 'سؤال' asked the agent to split one
// judgement ("not a deal") three ways at the exact moment they wanted to move
// on, and the three buckets were never acted on differently downstream. The
// backend rejects the retired values, so this list and its validation agree.
export const CLASSIFY_CHOICES = [
  {
    key: 'real',
    label: 'فرصة حقيقية',
    hint: 'عميل مهتم بطلب جديد — يدخل مسار المتابعة',
    tone: 'success',
    archives: false
  },
  {
    key: 'prior_followup',
    label: 'متابعة طلب سابق',
    hint: 'يسأل عن طلب مفتوح بالفعل — لا يُفتح طلب جديد',
    tone: 'info',
    archives: true
  }
];

export const STAGE_RANK = { new: 0, offer_sent: 1, serious: 2, contracted: 3 };
export const EXIT_STAGES = ['not_serious', 'lost'];
export const CLOSED_STAGES = ['contracted', 'not_serious', 'lost'];
export const LADDER = ['new', 'offer_sent', 'serious', 'contracted'];

// Mirrors backend/crm/utils/stages.js: strictly one rung up, plus the exits.
// The server re-checks every transition, so a stale copy here cannot let an
// illegal change through — it only decides which buttons to render.
export const nextStage = (from) => {
  if (CLOSED_STAGES.includes(from)) return null;
  const rank = STAGE_RANK[from];
  if (rank === undefined) return null;
  return LADDER.find((s) => STAGE_RANK[s] === rank + 1) || null;
};

export const stageBadgeStyle = (stage) => ({
  new: { background: '#e3f2fd', color: '#1565c0' },
  offer_sent: { background: '#fff3e0', color: '#e65100' },
  serious: { background: '#f3e5f5', color: '#6a1b9a' },
  contracted: { background: '#e8f5e9', color: '#2e7d32' },
  not_serious: { background: '#eceff1', color: '#546e7a' },
  lost: { background: '#ffebee', color: '#c62828' }
}[stage] || {});

// Token-driven equivalent of stageBadgeStyle: returns the calm-identity pill
// classes (crm.css) for a stage instead of an inline colour pair. Purely a
// display helper — same six stages, same meaning, central-token colours:
//   new → info · offer_sent → warn · serious → accent
//   contracted → success · not_serious → neutral · lost → danger
// stageBadgeStyle is kept for screens not yet migrated to the calm identity.
//   pending_classification → its own pill, deliberately NOT one of the stage
//   tones: this is a pre-stage state, and reusing a stage colour would read as
//   "another stage on the ladder" instead of "not on the ladder yet".
export const stagePillClass = (stage) => `crm-pill ${({
  new: 'crm-pill-info',
  offer_sent: 'crm-pill-warn',
  serious: 'crm-pill-accent',
  contracted: 'crm-pill-success',
  not_serious: 'crm-pill-neutral',
  lost: 'crm-pill-danger',
  pending_classification: 'crm-pill-pending'
}[stage]) || 'crm-pill-neutral'}`;

export const fmtHours = (h) => {
  if (h === null || h === undefined) return '—';
  if (h < 1) return `${Math.max(1, Math.round(h * 60))} د`;
  if (h < 24) return `${Math.round(h)} ساعة`;
  const d = Math.floor(h / 24);
  const r = Math.round(h % 24);
  return r ? `${d} ي ${r} س` : `${d} يوم`;
};

export const fmtDateTime = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return `${d.toLocaleDateString('en-GB')} ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
};

// Cross-page navigation inside the old app's activePage model: the target
// phone / opportunity is stashed here, then setActivePage switches the view.
export const crmNav = {
  setClient: (phone) => localStorage.setItem('crmClientPhone', phone || ''),
  getClient: () => localStorage.getItem('crmClientPhone') || '',
  setOpportunity: (id) => localStorage.setItem('crmOpportunityId', id ? String(id) : ''),
  getOpportunity: () => localStorage.getItem('crmOpportunityId') || '',

  // Batch 3: a one-shot message that survives a page switch. Archiving a
  // classification navigates away immediately, so the confirmation has to be
  // handed to the destination screen rather than shown on the page being left.
  // Same localStorage channel as the rest of crmNav; read-and-clear so it can
  // never reappear on a later visit.
  setFlash: (msg) => localStorage.setItem('crmFlash', msg || ''),
  takeFlash: () => {
    const m = localStorage.getItem('crmFlash') || '';
    if (m) localStorage.removeItem('crmFlash');
    return m;
  }
};

// Page switch via a window event rather than prop-threading, so existing pages
// (CallsPage) can navigate to CRM screens without changing how App.js renders
// them. App.js listens for this and calls its own setActivePage.
export const CRM_NAV_EVENT = 'crm-navigate';
export const crmGo = (page) =>
  window.dispatchEvent(new CustomEvent(CRM_NAV_EVENT, { detail: page }));
